import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Star } from 'lucide-react';

const QuestionReportCard = ({ question, location }) => {
    const { questionText, questionType, options, stats = { totalAnswers: 0, multiChoice: {}, starRating: {}, freeText: {} } } = question;

    if (!stats) {
        return (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-500">Veri yüklenemedi</p>
            </div>
        );
    }

    const renderMultiChoice = () => {
        const chartData = Object.entries(stats.multiChoice).map(([option, count]) => ({
            name: option,
            count: count
        }));

        return (
            <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {chartData.map((item, idx) => (
                        <div key={idx} className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg border border-blue-200">
                            <p className="text-xs text-gray-600 truncate">{item.name}</p>
                            <p className="text-2xl font-bold text-blue-600">{item.count}</p>
                        </div>
                    ))}
                </div>

                {chartData.length > 0 && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200 h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="count" fill="#3b82f6" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        );
    };

    const renderStarRating = () => {
        const chartData = [];
        for (let i = 1; i <= 5; i++) {
            chartData.push({
                stars: `${i} ${i === 1 ? 'Yıldız' : 'Yıldız'}`,
                count: stats.starRating[i] || 0
            });
        }

        const total = chartData.reduce((sum, item) => sum + item.count, 0);
        const average = total > 0
            ? (chartData.reduce((sum, item, idx) => sum + (item.count * (idx + 1)), 0) / total).toFixed(2)
            : 0;

        return (
            <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                    {chartData.map((item, idx) => (
                        <div key={idx} className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-3 rounded-lg border border-yellow-200">
                            <div className="flex gap-0.5 mb-1">
                                {[...Array(idx + 1)].map((_, i) => (
                                    <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                ))}
                            </div>
                            <p className="text-2xl font-bold text-yellow-600">{item.count}</p>
                        </div>
                    ))}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-lg border border-purple-200 flex flex-col justify-center">
                        <p className="text-gray-600 text-xs">Ortalama Puan</p>
                        <p className="text-2xl font-bold text-purple-600">{average}</p>
                    </div>
                </div>

                {chartData.length > 0 && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200 h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="stars" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="count" fill="#fbbf24" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        );
    };

    const renderFreeText = () => {
        const entries = Object.entries(stats.freeText).sort((a, b) => b[1] - a[1]);

        return (
            <div className="space-y-3 max-h-96 overflow-y-auto">
                {entries.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Yanıt yok</p>
                ) : (
                    entries.map((entry, idx) => (
                        <div key={idx} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                            <div className="flex-1 pr-3">
                                <p className="text-gray-700 break-words">{entry[0]}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                                    {entry[1]}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        );
    };

    return (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">{questionText}</h3>
                <p className="text-sm text-gray-600 mt-1">
                    Toplam Yanıt: <span className="font-semibold">{stats.totalAnswers}</span>
                </p>
            </div>

            <div className="p-6">
                {questionType === 3 && renderMultiChoice()}
                {questionType === 2 && renderStarRating()}
                {questionType === 1 && renderFreeText()}
            </div>
        </div>
    );
};

export default QuestionReportCard;
